
import React, { useState, useEffect } from 'react';
import { userApiClient } from '../../api/HttpClient';
import dayjs from 'dayjs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

interface PaymentRow {
  payment_id: string;
  tenant_id: string;
  tenant_name: string;
  room_code: string;
  rent_amount: number;
  due_date: string;
  month: string;
  is_paid: boolean;
  paid_at: string | null;
  communication_last_sent_at: string | null;
  tenant_join_date?: string;
}

interface PaymentsApiResponse {
  property_id: string;
  month: string;
  payments: PaymentRow[];
}

interface RentPaymentsTabProps {
  propertyId: string;
}

const RentPaymentsTab: React.FC<RentPaymentsTabProps> = ({ propertyId }) => {
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rentMonthFilter, setRentMonthFilter] = useState<string>(dayjs().format('YYYY-MM'));
  const [rentStatusFilter, setRentStatusFilter] = useState<string>('all');
  const [rentSelectedIds, setRentSelectedIds] = useState<string[]>([]);
  const [rentRowsPerPage, setRentRowsPerPage] = useState(10);
  const [rentCurrentPage, setRentCurrentPage] = useState(1);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const client = await userApiClient;
      const response = await client.get<PaymentsApiResponse>(`/api/pg_tenant/property/${propertyId}/payments/?month=${rentMonthFilter}&status=${rentStatusFilter}`);
      if (response && Array.isArray(response.payments)) {
        setPayments(response.payments);
      } else {
        setPayments([]);
      }
    } catch (err) {
      console.error("Failed to fetch payments:", err);
      setError("Failed to load payments.");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [propertyId, rentMonthFilter, rentStatusFilter]);

  const handleMarkPaid = async (paymentId: string) => {
    try {
      const client = await userApiClient;
      await client.post(`/api/pg_tenant/payment/${paymentId}/mark-done/`);
      fetchPayments();
    } catch (err) {
      console.error("Failed to mark payment as paid:", err);
    }
  };

  const handleBulkMarkPaid = async () => {
    if (!rentSelectedIds || rentSelectedIds.length === 0) {
      return;
    }
    setLoading(true);
    for (const paymentId of rentSelectedIds) {
      try {
        const client = await userApiClient;
        await client.post(`/api/pg_tenant/payment/${paymentId}/mark-done/`);
      } catch (err) {
        console.error(`Failed to mark payment ${paymentId} as paid:`, err);
      }
    }
    setLoading(false);
    setRentSelectedIds([]);
    fetchPayments();
  };

  const filteredPayments = payments.filter(p => {
    if (rentStatusFilter === 'all') return true;
    if (rentStatusFilter === 'paid') return p.is_paid;
    if (rentStatusFilter === 'due') return !p.is_paid && !dayjs(p.due_date).isBefore(dayjs(), 'day');
    if (rentStatusFilter === 'overdue') return !p.is_paid && dayjs(p.due_date).isBefore(dayjs(), 'day');
    return true;
  });

  const startIndex = (rentCurrentPage - 1) * rentRowsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + rentRowsPerPage);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Rent Payments</h2>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Month</Label>
          <Select value={rentMonthFilter} onValueChange={setRentMonthFilter}>
            <SelectTrigger className="w-[120px] bg-background">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent className="bg-card border z-50">
              {Array.from({ length: 13 }, (_, i) => dayjs().subtract(i, 'month').format('YYYY-MM')).map(month => (
                <SelectItem key={month} value={month}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={rentStatusFilter} onValueChange={setRentStatusFilter}>
            <SelectTrigger className="w-[100px] bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-card border z-50">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="due">Due</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {rentSelectedIds.length > 0 && (
          <Button onClick={handleBulkMarkPaid}>
            Mark {rentSelectedIds.length} Selected as Paid
          </Button>
        )}
      </div>

      {/* Rent Payments Table */}
      <div className="border border-border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-10">
                <Checkbox
                  checked={rentSelectedIds.length === filteredPayments.length && filteredPayments.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setRentSelectedIds(filteredPayments.map(p => p.payment_id));
                    } else {
                      setRentSelectedIds([]);
                    }
                  }}
                />
              </TableHead>
              <TableHead className="font-semibold">Tenant Name</TableHead>
              <TableHead className="font-semibold">Room/Bed</TableHead>
              <TableHead className="font-semibold">Join Date</TableHead>
              <TableHead className="font-semibold">Rent Amount</TableHead>
              <TableHead className="font-semibold">Due Date</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedPayments.map((payment) => (
              <TableRow key={payment.payment_id} className="hover:bg-muted/50">
                <TableCell>
                  <Checkbox
                    checked={rentSelectedIds.includes(payment.payment_id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setRentSelectedIds([...rentSelectedIds, payment.payment_id]);
                      } else {
                        setRentSelectedIds(rentSelectedIds.filter(id => id !== payment.payment_id));
                      }
                    }}
                  />
                </TableCell>
                <TableCell className="font-medium">{payment.tenant_name}</TableCell>
                <TableCell>{payment.room_code}</TableCell>
                <TableCell>{payment.tenant_join_date || 'N/A'}</TableCell>
                <TableCell>{(typeof payment.rent_amount === 'number' ? payment.rent_amount : parseFloat(payment.rent_amount as any)).toFixed(2)}</TableCell>
                <TableCell>{payment.due_date}</TableCell>
                <TableCell>
                  <span className={cn(
                    "font-medium",
                    !payment.is_paid && dayjs(payment.due_date).isBefore(dayjs(), 'day') && "text-red-500",
                    !payment.is_paid && !dayjs(payment.due_date).isBefore(dayjs(), 'day') && "text-orange-500",
                    payment.is_paid && "text-green-600"
                  )}>
                    {payment.is_paid ? 'Paid' : (dayjs(payment.due_date).isBefore(dayjs(), 'day') ? 'Overdue' : 'Due')}
                  </span>
                </TableCell>
                <TableCell>
                  {!payment.is_paid && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkPaid(payment.payment_id)}
                    >
                      MARK PAID
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end gap-4 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          <Select value={String(rentRowsPerPage)} onValueChange={(v) => { setRentRowsPerPage(Number(v)); setRentCurrentPage(1); }}>
            <SelectTrigger className="w-[70px] h-8 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border z-50">
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span>
          {`${(rentCurrentPage - 1) * rentRowsPerPage + 1}–${Math.min(rentCurrentPage * rentRowsPerPage, filteredPayments.length)} of ${filteredPayments.length}`}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={rentCurrentPage === 1}
            onClick={() => setRentCurrentPage(rentCurrentPage - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={rentCurrentPage * rentRowsPerPage >= filteredPayments.length}
            onClick={() => setRentCurrentPage(rentCurrentPage + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RentPaymentsTab;
